import "./App.css";
import "./styles/Base/Accesibilidad.css";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import userContext from "./components/Context/userContext";
import NavBarP from "./components/NavBar/NavBarP";
import Footer from "./components/Footer/Footer";
import AuthRoute from "./components/AuthRoute/AuthRoute";
import AdminRoute from "./components/AuthRoute/AdminRoute";
import Cargando from "./components/Comun/Cargando";
import ErrorBoundary from "./components/Comun/ErrorBoundary";
import { API, alCaducarSesion, getToken, guardarToken, limpiarSesion } from "./services/api";

// Cada página se descarga solo cuando se visita (React.lazy). El Suspense de abajo
// enseña «Cargando» mientras tanto y el ErrorBoundary avisa si no se pudo descargar.
const Home = lazy(() => import("./components/Home/Home"));
const Login = lazy(() => import("./components/Login/Login"));
const Logout = lazy(() => import("./components/Logout"));
const Registro = lazy(() => import("./components/Registro/Registro"));
const VerifyEmail = lazy(() => import("./components/Auth/VerifyEmail"));
const ReenviarVerificacionPagina = lazy(() => import("./components/Auth/ReenviarVerificacionPagina"));
const RecuperarContrasena = lazy(() => import("./components/Auth/RecuperarContrasena"));
const RestablecerContrasena = lazy(() => import("./components/Auth/RestablecerContrasena"));
const Contacto = lazy(() => import("./components/Contacto/Contacto"));
const ListaCompra = lazy(() => import("./components/Recipes/ListaCompra"));
const QueCocino = lazy(() => import("./components/Recipes/QueCocino"));
const Recipes = lazy(() => import("./components/Recipes/Recipes"));
const DetalleRecipe = lazy(() => import("./components/Recipes/DetalleRecipe"));
const ChangePassword = lazy(() => import("./components/ChangePassword/ChangePassword"));
const InfoDatosPersonales = lazy(() => import("./components/Profile/InfoDatosPersonales"));
const Profile = lazy(() => import("./components/Profile/Profile"));
const Delete = lazy(() => import("./components/Profile/Delete"));
const Admin = lazy(() => import("./components/Admin/Admin"));
const UpdateRecipe = lazy(() => import("./components/Admin/update/UpdateRecipe"));
const Privacidad = lazy(() => import("./components/PoliticayPrivacidad/Politica"));
const AvisoLegal = lazy(() => import("./components/PoliticayPrivacidad/AvisoLegal"));
const Cookies = lazy(() => import("./components/PoliticayPrivacidad/Cookies"));
const TerminosCondiciones = lazy(() => import("./components/PoliticayPrivacidad/TerminosCondiciones"));
const NotFound = lazy(() => import("./components/NotFound"));

// Título de la pestaña de las páginas que no lo ponen ellas mismas
const TITULOS = {
  "/": "Plato tras Plato · Recetas para cocinar con pasión",
  "/recipes": "Recetas · Plato tras Plato",
  // «/recipes/:slug» y «/recipes/id/:id» no están aquí: el título lo pone la propia receta al cargarse
  "/login": "Iniciar sesión · Plato tras Plato",
  "/register": "Crear cuenta · Plato tras Plato",
  "/contacto": "Contacto · Plato tras Plato",
  "/que-cocino": "¿Qué cocino hoy? · Plato tras Plato",
  "/compra": "Lista de la compra · Plato tras Plato",
  "/verify-email": "Verificar email · Plato tras Plato",
  "/reenviar-verificacion": "Reenviar verificación · Plato tras Plato",
  "/olvide-contrasena": "Recuperar contraseña · Plato tras Plato",
  "/restablecer-contrasena": "Restablecer contraseña · Plato tras Plato",
  "/infoDatosPersonales": "Mi cuenta · Plato tras Plato",
  "/profile": "Editar perfil · Plato tras Plato",
  "/changePassword": "Cambiar contraseña · Plato tras Plato",
  "/delete": "Eliminar cuenta · Plato tras Plato",
  "/logout": "Sesión cerrada · Plato tras Plato",
};

function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [user, setUser] = useState(null);
  // Si hay un token guardado, al recargar hay que comprobarlo antes de decidir si hay sesión
  const [cargandoSesion, setCargandoSesion] = useState(() => Boolean(getToken()));
  const hayUsuario = useRef(false);
  hayUsuario.current = Boolean(user);

  // ── Abrir y cerrar sesión ───────────────────────────────
  const iniciarSesion = useCallback((token, userInfo) => {
    guardarToken(token);
    setUser(userInfo);
  }, []);

  const cerrarSesion = useCallback(() => {
    limpiarSesion();
    setUser(null);
  }, []);

  // ── 401 de la API: sesión caducada o no válida ──────────
  // El interceptor de services/api.js ya ha borrado el token. Aquí se vacía el usuario y,
  // si había sesión abierta, se manda al login volviendo después a la página actual.
  // (El 403 no pasa por aquí: es «sin permiso», no «sesión caducada».)
  useEffect(() => {
    alCaducarSesion(() => {
      const habiaSesion = hayUsuario.current;
      setUser(null);
      setCargandoSesion(false);
      if (habiaSesion) {
        const redirectTo = window.location.pathname + window.location.search + window.location.hash;
        navigate("/login", {
          replace: true,
          state: { redirectTo, aviso: "Tu sesión ha caducado. Inicia sesión de nuevo." },
        });
      }
    });
    return () => alCaducarSesion(null);
  }, [navigate]);

  // ── Rehidratar la sesión al recargar ────────────────────
  useEffect(() => {
    if (!getToken()) {
      setCargandoSesion(false);
      return undefined;
    }
    let cancelado = false;
    API.post("/users/checksession")
      .then((res) => {
        if (!cancelado) setUser(res.data);
      })
      .catch(() => {
        // 401: el interceptor ya limpió la sesión. Otro error (API caída): se queda sin
        // usuario pero con el token, y al recargar se vuelve a intentar.
      })
      .finally(() => {
        if (!cancelado) setCargandoSesion(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  // ── Al cambiar de página: arriba del todo, título y foco al contenido ──
  useEffect(() => {
    window.scrollTo(0, 0);
    if (TITULOS[pathname]) document.title = TITULOS[pathname];
    const contenido = document.getElementById("contenido");
    if (contenido && !window.location.hash) contenido.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <>
      <div className="App">
        <a href="#contenido" className="saltar-contenido">
          Saltar al contenido
        </a>

        <userContext.Provider value={{ user, setUser, cargandoSesion, iniciarSesion, cerrarSesion }}>
          <header className="div_header">
            <NavBarP user={user} />
          </header>

          <main id="contenido" className="contenido-principal" tabIndex={-1}>
            <ErrorBoundary reinicio={pathname}>
              <Suspense fallback={<Cargando texto="Cargando la página…" />}>
                <Routes>
                  <Route path="/" element={<Home user={user} />} />

                  {/* Cuenta */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/logout" element={<Logout />} />
                  <Route path="/register" element={<Registro />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/reenviar-verificacion" element={<ReenviarVerificacionPagina />} />
                  <Route path="/olvide-contrasena" element={<RecuperarContrasena />} />
                  <Route path="/restablecer-contrasena" element={<RestablecerContrasena />} />

                  {/* Recetas: la lista y el detalle son públicos. Solo «me gusta» y guardar
                      exigen cuenta (DetalleRecipe manda al login y vuelve a la receta). */}
                  <Route path="/recipes" element={<Recipes user={user} />} />
                  {/* La dirección bonita (/recipes/tarta-de-queso) y la antigua con id, que salta sola a la bonita */}
                  <Route path="/recipes/id/:id" element={<DetalleRecipe user={user} />} />
                  <Route path="/recipes/:slug" element={<DetalleRecipe user={user} />} />
                  <Route
                    path="/recipes/update/:id"
                    element={<AdminRoute user={user} component={<UpdateRecipe user={user} />} />}
                  />

                  <Route path="/contacto" element={<Contacto />} />
                  <Route path="/que-cocino" element={<QueCocino />} />
                  <Route path="/compra" element={<ListaCompra />} />

                  {/* Perfil */}
                  <Route
                    path="/changePassword"
                    element={<AuthRoute user={user} component={<ChangePassword user={user} />} />}
                  />
                  <Route
                    path="/infoDatosPersonales"
                    element={<AuthRoute user={user} component={<InfoDatosPersonales user={user} />} />}
                  />
                  <Route
                    path="/profile"
                    element={<AuthRoute user={user} component={<Profile user={user} />} />}
                  />
                  <Route
                    path="/delete"
                    element={<AuthRoute user={user} component={<Delete user={user} />} />}
                  />

                  {/* Admin: /admin/* lo gestiona el propio Admin (home, users, post).
                      Las rutas antiguas se redirigen a las nuevas. */}
                  <Route path="/admin/home" element={<Navigate to="/admin" replace />} />
                  <Route path="/users/pruebaAll" element={<Navigate to="/admin/users" replace />} />
                  <Route
                    path="/admin/*"
                    element={<AdminRoute user={user} component={<Admin user={user} />} />}
                  />

                  {/* Páginas legales (/politica era la antigua) */}
                  <Route path="/aviso-legal" element={<AvisoLegal />} />
                  <Route path="/privacidad" element={<Privacidad />} />
                  <Route path="/politica" element={<Navigate to="/privacidad" replace />} />
                  <Route path="/cookies" element={<Cookies />} />
                  <Route path="/terminos" element={<TerminosCondiciones />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </main>

          <Footer />
        </userContext.Provider>
      </div>
      <Analytics />
    </>
  );
}

export default App;
